import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2Icon } from 'lucide-react'

import { Button } from '../../ui/button'
import {
  ChroniqueurResponseInterface,
  InfluencedFileOutputsInterface,
  InfluencerData,
  InfluencerTableFilters,
} from '../../../core/interface/chronique.interface'
import InfluencerTableViewContainer from '../influencerTable/InfluencerTableViewContainer'
import PostGenerateWordCloud from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-generate-word-cloud.service'
import WordCloudModal from './WordCloudModal'
import PostImportInfluencer from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-import-influencer.service'
import PostImportInfluencerPromises from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/post-import-influencer-promises.service'
import { TableViewTypeInfluencerEnum } from '../../../core/enums/table-view-type-influencer.enum'
import DownloadCsvUtil from '../../../core/utils/downloadCsv.util'
import GetSelectedInfluencer from '../../../core/api/services/bookmetrie/private-reports/chroniqueur/get-selected-influencer.service'
import GLFileInput from '../../share/GLFileInput'

const Chroniqueur = ({
  chronique,
  setChronique,
  showFilters,
  selectedUsers,
  setSelectedUsers,
  headerFilters,
  setHeaderFilters,
  onValidate,
  setInfluencerFileImported,
  setChroniqueFileImported,
  setWordCloudGenerated,
  chroniqueImportedFiles,
  promisesImportedFiles,
}: {
  chronique: ChroniqueurResponseInterface
  setChronique: (chronique: ChroniqueurResponseInterface) => void
  showFilters: boolean
  selectedUsers: InfluencerData[]
  setSelectedUsers: (users: InfluencerData[]) => void
  headerFilters: InfluencerTableFilters
  setHeaderFilters: (filters: InfluencerTableFilters) => void
  onValidate: (selectedUsers: InfluencerData[]) => void
  setInfluencerFileImported: (imported: boolean) => void
  setChroniqueFileImported: (imported: boolean) => void
  setWordCloudGenerated: (generated: boolean) => void
  chroniqueImportedFiles: InfluencedFileOutputsInterface[]
  promisesImportedFiles: InfluencedFileOutputsInterface[]
}) => {
  const { t } = useTranslation()
  const [influencerFile, setInfluencerFile] = useState<File | null>(null)
  const [loadingInfluencer, setLoadingInfluencer] = useState<boolean>(false)
  const [promisesInfluencerFile, setPromisesInfluencerFile] =
    useState<File | null>(null)
  const [loadingPromisesInfluencer, setLoadingPromisesInfluencer] =
    useState<boolean>(false)
  const [userSelected, setUserSelected] = useState<InfluencerData | null>(null)
  const [wordCloudUrl, setWordCloudUrl] = useState<string | null>(
    chronique.word_cloud,
  )
  const [loadingWordCloud, setLoadingWordCloud] = useState<boolean>(false)
  const [loadingSelectedUsers, setLoadingSelectedUsers] =
    useState<boolean>(false)
  const [showWordCloudModal, setShowWordCloudModal] = useState<boolean>(false)

  const idEanStr = chronique.id_ean ? chronique.id_ean.toString() : ''

  if (!chronique) {
    return <div>{t('loading')}</div>
  }

  const handleInfluencerFileUpdate = () => {
    if (!influencerFile) return
    setLoadingInfluencer(true)

    PostImportInfluencer(idEanStr, influencerFile)
      .then((response: any) => {
        if (response?.data) {
          const data = response.data as ChroniqueurResponseInterface
          setChronique(data)
          setInfluencerFileImported(true)
          toast.success('Fichier XLSX mis à jour avec succès')
        }
      })
      .catch(() => {
        toast.error('Erreur lors de la mise à jour du fichier XLSX.')
      })
      .finally(() => {
        setLoadingInfluencer(false)
      })
  }

  const handlePromisesInfluencerFileUpdate = () => {
    if (!promisesInfluencerFile) return
    setLoadingPromisesInfluencer(true)

    PostImportInfluencerPromises(idEanStr, promisesInfluencerFile)
      .then((response: any) => {
        if (response?.data) {
          const data = response.data as ChroniqueurResponseInterface
          setChronique(data)
          setChroniqueFileImported(true)
          toast.success('Fichier XLSX mis à jour avec succès')
        }
      })
      .catch(() => {
        toast.error('Erreur lors de la mise à jour du fichier XLSX.')
      })
      .finally(() => {
        setLoadingPromisesInfluencer(false)
      })
  }

  const handleGenerateWordCloud = () => {
    if (wordCloudUrl) {
      setShowWordCloudModal(true)
      return
    }
    setLoadingWordCloud(true)
    PostGenerateWordCloud('', idEanStr, true)
      .then((response: any) => {
        if (response?.data) {
          setWordCloudUrl(response.data.word_cloud_url)
          setShowWordCloudModal(true)
          setWordCloudGenerated(true)
        }
      })
      .catch(() => {
        toast.error(
          "Erreur lors de la génération du nuage de mots. Le livre n'a peut-être pas de commentaires.",
        )
      })
      .finally(() => {
        setLoadingWordCloud(false)
      })
  }

  const handleDownloadSelectedUsers = () => {
    setLoadingSelectedUsers(true)
    GetSelectedInfluencer(idEanStr)
      .then((response: any) => {
        if (response?.data) {
          DownloadCsvUtil(response.data, `chroniqueurs_${idEanStr}.csv`)
        }
      })
      .catch(() => {
        toast.error(
          'Une erreur est survenue lors du téléchargement des influenceurs.',
        )
      })
      .finally(() => {
        setLoadingSelectedUsers(false)
      })
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Inputs et boutons */}
      <div className="px-6 py-4 space-y-3">
        <div className="flex items-center gap-3">
          <span>{t('chroniqueurView.progressBar.3')}</span>
          <GLFileInput
            id="influencer-file-input"
            selectedFile={influencerFile}
            onFileChange={setInfluencerFile}
            importedFile={chroniqueImportedFiles}
            placeholder={t('chroniqueurView.selectFilePlaceholder')}
            accept=".xlsx,.xls"
            disabled={loadingInfluencer}
          />
          <Button
            onClick={handleInfluencerFileUpdate}
            disabled={!influencerFile || loadingInfluencer}
          >
            {loadingInfluencer && <Loader2Icon className="animate-spin" />}
            {loadingInfluencer
              ? t('chroniqueurView.loading')
              : t('chroniqueurView.update')}
          </Button>
          <Button
            onClick={handleDownloadSelectedUsers}
            disabled={loadingSelectedUsers}
          >
            {loadingSelectedUsers && <Loader2Icon className="animate-spin" />}
            {loadingSelectedUsers
              ? t('chroniqueurView.loading')
              : t('chroniqueurView.downloadInfluencer')}
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <span>{t('chroniqueurView.progressBar.5')}</span>
          <GLFileInput
            id="promises-influencer-file-input"
            selectedFile={promisesInfluencerFile}
            onFileChange={setPromisesInfluencerFile}
            importedFile={promisesImportedFiles}
            placeholder={t('chroniqueurView.selectFilePlaceholder')}
            accept=".xlsx,.xls"
            disabled={loadingPromisesInfluencer}
          />
          <Button
            onClick={handlePromisesInfluencerFileUpdate}
            disabled={!promisesInfluencerFile || loadingPromisesInfluencer}
          >
            {loadingPromisesInfluencer && (
              <Loader2Icon className="animate-spin" />
            )}
            {loadingPromisesInfluencer
              ? t('chroniqueurView.loading')
              : t('chroniqueurView.update')}
          </Button>
          <Button onClick={handleGenerateWordCloud} disabled={loadingWordCloud}>
            {loadingWordCloud && <Loader2Icon className="animate-spin" />}
            {(() => {
              if (loadingWordCloud) return t('chroniqueurView.loading')
              if (wordCloudUrl) return t('chroniqueurView.wordCloud.read')
              return t('chroniqueurView.wordCloud.generate')
            })()}
          </Button>
        </div>
      </div>
      <InfluencerTableViewContainer
        viewType={TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW}
        chroniqueurId={idEanStr}
        data={chronique.influencers}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        userSelected={userSelected}
        setUserSelected={setUserSelected}
        showFilters={showFilters}
        onValidate={onValidate}
        headerFilters={headerFilters}
        setHeaderFilters={setHeaderFilters}
      />

      {showWordCloudModal && (
        <WordCloudModal
          url={wordCloudUrl}
          onClose={() => setShowWordCloudModal(false)}
          setWordCloudUrl={setWordCloudUrl}
          idEan={idEanStr}
        />
      )}
    </div>
  )
}

export default Chroniqueur
